import { useState, useRef } from 'react'
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import Bookmark from '@mui/icons-material/Bookmark'
import BookmarkBorder from '@mui/icons-material/BookmarkBorder'
import Check from '@mui/icons-material/Check'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import NotInterestedIcon from '@mui/icons-material/NotInterested'
import PermContactCalendar from '@mui/icons-material/PermContactCalendar'
import PlaceIcon from '@mui/icons-material/Place'
import RoofingIcon from '@mui/icons-material/Roofing'
import InventoryIcon from '@mui/icons-material/Inventory'
import PhonelinkRingIcon from '@mui/icons-material/PhonelinkRing'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineOutlined'
import SignalCellular1BarOutlinedIcon from '@mui/icons-material/SignalCellular1BarOutlined'
import SignalCellular4BarOutlinedIcon from '@mui/icons-material/SignalCellular4BarOutlined'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Rating,
  styled,
  Typography,
  colors,
  Collapse,
} from '@mui/material'
import type { FC } from 'react'
import { useDispatch } from 'react-redux'

import { formatPhoneNumber } from '~/shared/lib/displayText'
import { formatApiDate } from '~/shared/lib/date'
import {
  AgreementTypeConfig,
  asRecommendationStatus,
  getNetworkPartnerDisplayName,
  ReasonForException,
  SteerageProvider,
  SteerageProviderDataSource,
  SteerageProviderStatus,
  useAddProviderToSteerage,
  useDeleteProviderFromSteerage,
  useReviewSteerageProviders,
} from '~/entities/steerage'
import {
  referralSteerage,
  referralSteerageProviders,
  type ReferralSearchRibbonProvidersCreateResponse,
  type StatesListResponse,
} from '@fireflyhealth/api'
import UpdateProviderDetailsModal from './UpdateProviderDetailsModal'
import {
  DataCurationUsagePayload,
  EditProviderAndAvailabilityPayload,
  logEvent,
  SteerageAddProviderFromSearchPayload,
  SteerageRemoveProviderFromSearchPayload,
} from '~/shared/lib/events'
import { UpdateProviderAvailabilityModal } from './UpdateProviderAvailabilityModal'
import { Loader } from '~/shared/ui'
import { SpecialtyGroupSearchCategory } from './ProviderSearchContent'
import {
  ProviderListItemReasonModal,
  ProviderListItemReasonModalHandle,
} from './ProviderListItemReasonModal'
import { queueNotification } from '~/shared/notifications/actions'
import { CPTCode, ProviderRecommendationStatus } from './types'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

const RECOMMENDATION_TEXT_SX = {
  fontWeight: 500,
  fontSize: 'small',
  color: 'secondary.main',
  mt: '2px',
}

const SOFT_SX = {
  color: 'secondary.main',
  whiteSpace: 'normal',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}

const AVAILABILITY_ICON_SX = { mb: '-6px' }

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.grey[600],
  },
}))

const editProviderModalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: '24px',
  width: '490px',
}

const editAvailabilityModalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: '16px',
  width: '490px',
}

// A single provider search-result row, exactly as the generated @fireflyhealth/api
// client delivers it (ribbon search, talon procedure search, and the steerage
// provider `search_result` field all share this `_SteerageProviderSearchResult`
// shape). Derived from the generated response so the wire contract is the single
// source of truth — no hand-maintained restatement.
export type ProviderDetailWithAdditionalData = NonNullable<
  ReferralSearchRibbonProvidersCreateResponse['results']
>[number]

type ProviderSearchLocation = NonNullable<ProviderDetailWithAdditionalData['locations']>[number]

interface ProviderListItemProps {
  provider: ProviderDetailWithAdditionalData
  locationType: string | undefined | null
  npi?: string | null | undefined
  providerName?: string | null | undefined
  facilityName?: string | null | undefined
  steerageID?: number | null
  steerageProviderList?: Array<SteerageProvider>
  states?: StatesListResponse
  specialtyGroupSearchCategory: SpecialtyGroupSearchCategory | null
  providerSearchType: 'provider' | 'facility' | 'procedure'
  indexInResults: number | null
  searchRequestId: number | null
  isSystemSuggested?: boolean
  steerageProvider?: SteerageProvider
  cptCode: CPTCode | null
}

// Regex to format the incoming latitude and longitude from the vendors to
// Matches optional minus, up to 8 digits, optional decimal with up to 6 digits.
const GEO_COORDINATE_REGEX = /^-?\d{0,8}(\.\d{0,6})?/

/**
 * The primary location's display fields, flattened out of the vendor's nested
 * location record. Returns named fields rather than a positional tuple so callers
 * take only what they read and cannot be silently reordered; the return type is
 * inferred from the fields below.
 */
export const extractLocationDetails = (locations: ProviderSearchLocation[] | null | undefined) => {
  const location = locations && locations.length >= 1 ? locations[0] : null

  let phone = location?.phone_numbers
    ? location.phone_numbers.find(e => e?.details && e.details === 'primary')?.phone
    : null
  phone = formatPhoneNumber(phone)
  const faxDetails = location?.fax_numbers?.filter(e => e?.details && e.details === 'primary')
  const faxNumbers = faxDetails?.map(faxDetail => formatPhoneNumber(faxDetail.fax)) ?? []
  const address = location?.address_details
  const distance = location?.distance
  const name = location?.name
  const addedOn = location?.availability?.added_on
  const verifiedOn = formatApiDate(addedOn, 'MMM d, yyyy')
  const daysTillNextAvailability = location?.availability?.days_till_next_availability
  const networkPartnerAgreementType = location?.network_partner_agreement_type
  const doesProviderExist = location?.availability?.does_provider_exist
  const tins =
    location && 'tins' in location && location?.tins && location?.tins?.length > 0
      ? location?.tins
      : []
  const organizationNpis =
    location &&
    'organization_npis' in location &&
    location?.organization_npis &&
    location?.organization_npis?.length > 0
      ? location?.organization_npis
      : []
  const talonAssociatedProcedurePrices = location?.talon_associated_procedure_prices ?? null
  return {
    name,
    address,
    distance,
    phone,
    faxNumbers,
    locationInNetwork: location?.location_in_network == true,
    addedOn,
    verifiedOn,
    daysTillNextAvailability,
    networkPartnerAgreementType,
    doesProviderExist,
    tins,
    talonAssociatedProcedurePrices,
    organizationNpis,
  }
}

export const ProviderListItem: FC<ProviderListItemProps> = props => {
  const [approved, setApproved] = useState(false)
  const [hide, setHide] = useState(false)
  const dispatch = useDispatch()
  const client = useQueryClient()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const costScore = props?.provider?.firefly_recommendation_factors?.cost_score_out_of_five
  const encounterPrice = props?.provider?.talon_recommendation_factors?.encounter_price
  let invertedCostScore: number | undefined = undefined

  if (costScore != null) {
    // cost ratings range from 1 - 5
    // a cost rating of 1 indicates a "costly" provider and should be
    // represented by $$$$$ (five dollar icons)
    // a cost rating of 5 indicates a "low cost" provider and should be
    // represented by $ (one dollar icon)
    invertedCostScore = Math.floor(6 - costScore)
  }
  const qualityScore = props?.provider?.firefly_recommendation_factors?.quality_score_out_of_five
  const numberOfRatings = props?.provider?.firefly_recommendation_factors?.number_of_ratings
  const averageRating = props?.provider?.firefly_recommendation_factors?.average_rating_out_of_five
  const recommendationStatus = props?.provider?.recommendation_status
  const recommendationReasonLabels = props?.provider?.recommendation_reason_labels
  const expertiseScore =
    props?.provider?.firefly_recommendation_factors?.expertise_score_out_of_five

  const hasLocation = Boolean(props?.provider?.locations && props?.provider?.locations?.length >= 1)
  const locationConfidenceScore = hasLocation
    ? (props?.provider?.locations?.[0]?.confidence ?? null)
    : null
  const isVerified = hasLocation ? (props?.provider?.locations?.[0]?.is_verified ?? false) : false
  const partnerName = hasLocation ? (props?.provider?.locations?.[0]?.partner_name ?? null) : null
  const geoCode = hasLocation ? (props?.provider?.locations?.[0]?.geo_code ?? null) : null

  const {
    name: locationName,
    address,
    distance,
    phone,
    faxNumbers,
    locationInNetwork,
    addedOn,
    verifiedOn,
    daysTillNextAvailability,
    networkPartnerAgreementType,
    doesProviderExist,
    tins,
    talonAssociatedProcedurePrices,
    organizationNpis,
  } = extractLocationDetails(props?.provider?.locations ?? null)

  const isUnknownRecommendation =
    recommendationStatus == null ||
    recommendationStatus == 'NO_RATING' ||
    recommendationStatus == 'NEUTRAL'
  const isRecommended = recommendationStatus == 'RECOMMENDED'
  const isNotRecommended = recommendationStatus == 'NOT_RECOMMENDED'

  // Use the search term specialty group to show as department in the data curation model
  let specialtyGroupSearchedTerm: { id: number; label: string } | null = null
  if (props?.specialtyGroupSearchCategory) {
    specialtyGroupSearchedTerm = {
      id: props?.specialtyGroupSearchCategory?.id,
      label: props?.specialtyGroupSearchCategory?.label,
    }
  }

  // For opening and closing the edit provider modal
  const [openProviderDetails, setOpenProviderDetails] = useState(false)
  const handleEditProviderDetailsOpen = () => {
    // Close the menu before opening the modal. Both install their own focus
    // trap on `document`; leaving the menu open keeps two traps active at once,
    // and they fight each other for focus indefinitely.
    handleClose()
    setOpenProviderDetails(true)
    // Log the opening of the edit contact details modal to full story
    void logEvent<EditProviderAndAvailabilityPayload>('EDIT_PROVIDER_DETAILS_BUTTON_CLICK', {
      providerNpi: props?.npi ?? null,
      careOrganisationName: props?.facilityName ?? null,
      steerageId: props?.steerageID,
      isEditedInDirectory: true,
      isProviderSearch: props?.npi ? true : false,
    })
  }
  const handleEditProviderDetailsClose = () => {
    setOpenProviderDetails(false)
    handleClose()
  }

  const handleAvailabilityModalOpen = () => {
    // Close the menu before opening the modal so the menu's focus trap does not
    // contend with the modal's (see handleEditProviderDetailsOpen).
    handleClose()
    setModalOpen(!modalOpen)
    // Log the opening of the edit Availability modal to full story
    void logEvent<EditProviderAndAvailabilityPayload>('EDIT_AVAILABILITY_BUTTON_CLICK', {
      providerNpi: props?.npi ?? null,
      careOrganisationName: props?.facilityName ?? null,
      steerageId: props?.steerageID,
      isEditedInDirectory: true,
      isProviderSearch: props?.npi ? true : false,
    })
  }

  const { isPending: isAddingProvider, mutateAsync: handleAddProviderToSteerage } =
    useAddProviderToSteerage()
  const { isPending: isDeletingProvider, mutate: deleteSteerageProvider } =
    useDeleteProviderFromSteerage()

  const handleAvailabilityModal = () => {
    setModalOpen(!modalOpen)
    handleClose()
  }

  const handleUserClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const steerageProviderMatchingID = props.steerageProviderList?.find(steerageProvider => {
    // The unique identifier is sent back as the construction of the NPI & Location unique keys
    // for Providers and only the Location unique keys for facilities. We save this as the source
    // identifier, when we add from this widget.
    if (
      props.provider.unique_identifier == steerageProvider.source_identifier &&
      steerageProvider.status !== SteerageProviderStatus.Rejected
    )
      return true
    return false
  })?.id

  const deleteProvider = (providerID: number) => {
    if (props.steerageID == null) return
    // calling the delete api for deleting provider from the care key
    const payload = {
      steerage_id: props.steerageID,
      id: providerID,
    }
    deleteSteerageProvider(payload, {
      // The mutation's own failure is handled by its hook; this refresh is separate work that can
      // fail on its own, and `mutate`'s onSuccess is typed to return void so it is never awaited.
      onSuccess: () =>
        fireAndForget('[Steerage] refresh after removing provider', async () => {
          // invalidate queries for fetching the care key
          await client.cancelQueries({
            queryKey: referralSteerage.retrieveQueryKey({ id: props.steerageID ?? 0 }),
          })
          await client.invalidateQueries({
            queryKey: referralSteerage.retrieveQueryKey({ id: props.steerageID ?? 0 }),
          })
          await client.cancelQueries({
            queryKey: referralSteerageProviders.listQueryKey({
              steerage_id: props.steerageID ?? 0,
            }),
          })
          await client.invalidateQueries({
            queryKey: referralSteerageProviders.listQueryKey({
              steerage_id: props.steerageID ?? 0,
            }),
          })
          if (props.steerageID != null) {
            void logEvent<SteerageRemoveProviderFromSearchPayload>(
              'STEERAGE_REMOVE_PROVIDER_FROM_STEERAGE_VIA_SEARCH',
              {
                steerageId: props.steerageID,
                positionOfProvider: props?.indexInResults,
                steerageProviderId: providerID,
              },
            )
          }
        }),
    })
  }

  const addProviderToSteerage = async () => {
    if (props.steerageID == null) {
      return
    }

    // Get exception reason from the modal if the provider is unknown or not recommended
    let exception: ReasonForException | null = null
    if (isNotRecommended || isUnknownRecommendation) {
      exception = ((await providerReasonRef.current?.chooseReason()) ??
        null) as ReasonForException | null
      if (exception === null) {
        return
      }
    }

    const state =
      props.states && address?.state
        ? props.states.find(stateDetail => stateDetail.abbreviation === address.state)?.id
        : null

    // The endpoint types this as a plain string, so an unrecognised status is sent as no status
    // rather than claimed to be one the steerage understands. It is logged because dropping it is
    // the wrong answer if the backend has added a status this list has not caught up with.
    const rawRecommendationStatus = props.provider.recommendation_status
    const recommendationStatusKey = asRecommendationStatus(rawRecommendationStatus)
    if (rawRecommendationStatus && recommendationStatusKey === null) {
      logger.warn('[ProviderSearch] Dropping an unrecognised recommendation status')
    }

    await handleAddProviderToSteerage({
      steerage_id: props.steerageID,
      first_name: props.provider.first_name,
      last_name: props.provider.last_name,
      npi: props.provider.npi != null ? String(props.provider.npi) : null,
      specialty_list:
        props.provider.specialties != null && props.provider.specialties.length != 0
          ? props.provider.specialties
          : null,
      care_organization_name: props.provider.npi ? locationName : props.provider.name,
      address_line_1: address?.address_line_1,
      address_line_2: address?.address_line_2,
      city: address?.city,
      cpt_code: props?.cptCode?.code != null ? String(props?.cptCode?.code) : null,
      state_id: state,
      zip_code: address?.zip != null ? String(address?.zip) : null,
      phone: phone?.replace(/\D/g, '') ?? null,
      fax: faxNumbers[0]?.replace(/\D/g, '') ?? null,
      data_source: props?.cptCode?.code
        ? SteerageProviderDataSource.Talon
        : SteerageProviderDataSource.Ribbon,
      source_identifier: props.provider.unique_identifier,
      network_partner_agreement_type: networkPartnerAgreementType as AgreementTypeConfig | null,
      specialty_group: props?.specialtyGroupSearchCategory?.id ?? null,
      composite_score: props?.provider?.composite_score ?? null,
      cost_score: costScore,
      quality_score: qualityScore,
      number_of_rating: numberOfRatings,
      average_rating: averageRating,
      cost_breakdown: talonAssociatedProcedurePrices ?? null,
      estimated_cost: props?.provider?.talon_recommendation_factors?.encounter_price,
      expertise_score: props?.provider?.talon_recommendation_factors?.expertise_score,
      recommendation_reason: recommendationReasonLabels
        ? recommendationReasonLabels.split(',').map(reason => reason.trim())
        : null,
      search_request: props?.searchRequestId,
      languages: props?.provider?.languages,
      location_confidence_score: locationConfidenceScore,
      distance: distance,
      tins: tins,
      organization_npis: organizationNpis,
      latitude: geoCode?.latitude
        ? (geoCode.latitude.toString().match(GEO_COORDINATE_REGEX)?.[0] ?? null)
        : null,
      longitude: geoCode?.longitude
        ? (geoCode.longitude.toString().match(GEO_COORDINATE_REGEX)?.[0] ?? null)
        : null,
      reason_for_exception: exception,
      has_verified_contact_details: isVerified,
      recommendation_status: recommendationStatusKey,
    }).then(async storageResult => {
      // invalidate queries for refetching the providers
      await client.cancelQueries({
        queryKey: referralSteerage.retrieveQueryKey({ id: props.steerageID ?? 0 }),
      })
      await client.invalidateQueries({
        queryKey: referralSteerage.retrieveQueryKey({ id: props.steerageID ?? 0 }),
      })
      await client.cancelQueries({
        queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props.steerageID ?? 0 }),
      })
      await client.invalidateQueries({
        queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props.steerageID ?? 0 }),
      })
      if (props.steerageID != null) {
        void logEvent<SteerageAddProviderFromSearchPayload>(
          'STEERAGE_ADD_PROVIDER_TO_STEERAGE_VIA_SEARCH',
          {
            steerageId: props.steerageID,
            positionOfProvider: props.indexInResults,
            steerageProviderId: storageResult.id,
          },
        )
        void logEvent<DataCurationUsagePayload>('DATA_CURATION_USAGE', {
          steerageId: props.steerageID,
          steerageProviderId: storageResult.id,
          isAvailabilityPresent: addedOn ? true : false,
          isContactDetailsVerified: isVerified ? true : false,
          isProviderSearch: props?.providerSearchType == 'provider' ? true : false,
        })
      }
    })
  }

  const providerReasonRef = useRef<ProviderListItemReasonModalHandle>(null)

  const { mutate: reviewSuggestion, isPending: reviewSuggestionLoading } =
    useReviewSteerageProviders(props.steerageID, {
      onMutate: payload => {
        setApproved(payload.approve)
        setTimeout(() => {
          setHide(true)
        }, 1000)
      },
      onError: () => {
        setApproved(false)
        setHide(false)
        dispatch(
          queueNotification({
            variant: 'error',
            message: 'Failed to review the suggestion',
          }),
        )
      },
    })
  const getFirstFiveZipStrict = (addr?: { zip?: string | null } | null) => {
    const zipCode = addr?.zip
    if (zipCode && typeof zipCode === 'string' && zipCode.length >= 5) {
      return zipCode.slice(0, 5)
    }
    return addr?.zip
  }

  const handleAcceptSuggestion = () => {
    // The accept/dismiss UI only renders when a steerageProvider is present;
    // guard so a missing one can't submit `[undefined]` as the provider id.
    const steerageProviderId = props.steerageProvider?.id
    if (steerageProviderId == null) return
    reviewSuggestion({ providers: [steerageProviderId], approve: true })
  }

  const handleDismissSuggestion = async () => {
    if (!props.steerageProvider) return
    // Get exception reason from the modal if the provider is unknown or not recommended
    let exception: string | null = null
    exception = (await providerReasonRef.current?.chooseReason()) ?? null
    if (exception === null) {
      return
    }
    reviewSuggestion({
      providers: [props.steerageProvider.id],
      approve: false,
      reason_for_rejection: exception,
    })
  }

  return (
    <Box>
      <ProviderListItemReasonModal
        ref={providerReasonRef}
        providerSearchType={props.providerSearchType}
        isSystemSuggested={props.isSystemSuggested}
        providers={[props.provider]}
      />
      <Collapse in={!hide}>
        <Box sx={{ px: 2, pt: 1 }}>
          {isAddingProvider || isDeletingProvider ? <Loader /> : null}
          <Divider />
          <Modal
            open={openProviderDetails}
            onClose={(_, reason) => {
              // v9 removed `disableEscapeKeyDown`; declining the
              // escapeKeyDown reason keeps Escape from closing the modal.
              if (reason === 'escapeKeyDown') return
              handleEditProviderDetailsClose()
            }}
            disableScrollLock
            hideBackdrop
          >
            <Box sx={editProviderModalStyle}>
              <UpdateProviderDetailsModal
                provider={props.provider}
                specialtyGroups={specialtyGroupSearchedTerm ? [specialtyGroupSearchedTerm] : null}
                handleEditProviderDetailsClose={() => handleEditProviderDetailsClose()}
                steerageId={props?.steerageID}
                providerSearchType={props.providerSearchType}
                isEditedInDirectory={true}
              />
            </Box>
          </Modal>

          <Box>
            <Modal
              open={modalOpen}
              onClose={(_, reason) => {
                // v9 removed `disableEscapeKeyDown`; declining the
                // escapeKeyDown reason keeps Escape from closing the modal.
                if (reason === 'escapeKeyDown') return
                handleAvailabilityModal()
              }}
              disableScrollLock
              hideBackdrop
            >
              <Box sx={editAvailabilityModalStyle}>
                <UpdateProviderAvailabilityModal
                  provider={props.provider}
                  handleCloseAvailabilityModal={() => handleAvailabilityModal()}
                  providerSearchType={props.providerSearchType}
                  steerageId={props?.steerageID}
                  isEditedInDirectory={true}
                  specialtyGroups={specialtyGroupSearchedTerm ? [specialtyGroupSearchedTerm] : null}
                />
              </Box>
            </Modal>
          </Box>

          <Grid container direction="row" sx={{ alignItems: 'flex-start' }}>
            {props.steerageID && !props.isSystemSuggested ? (
              <Grid>
                <Box sx={{ py: 1, mt: 1 }}>
                  {steerageProviderMatchingID ? (
                    <IconButton
                      disabled={isDeletingProvider || isAddingProvider}
                      onClick={() => deleteProvider(steerageProviderMatchingID)}
                    >
                      <Bookmark color="primary" />
                    </IconButton>
                  ) : (
                    <IconButton
                      disabled={isDeletingProvider || isAddingProvider}
                      onClick={() =>
                        fireAndForget(
                          '[ProviderSearch] add provider to steerage',
                          addProviderToSteerage,
                        )
                      }
                    >
                      <BookmarkBorder />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ) : null}
            <Grid sx={{ flex: 1 }}>
              <Box sx={{ pl: 1 }}>
                {props.isSystemSuggested ? (
                  <Grid
                    container
                    columnSpacing={1}
                    sx={{ bgcolor: approved ? colors.grey[50] : colors.blue[50], py: 1, mb: 1 }}
                  >
                    <Grid
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: 1,
                        color: theme => theme.palette.grey[600],
                      }}
                    >
                      <Typography sx={{ ml: 1, my: 1 }} variant="subtitle2">
                        System Suggested
                      </Typography>
                      <AutoAwesome sx={{ mx: 1 }} color="inherit" fontSize="small" />
                      <Typography variant="caption">{recommendationReasonLabels}</Typography>
                    </Grid>
                    {approved ? (
                      <Grid
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          color: theme => theme.palette.grey[600],
                        }}
                      >
                        <Check color="inherit" />
                        <Typography variant="subtitle2" sx={{ mr: 2 }}>
                          Added
                        </Typography>
                      </Grid>
                    ) : (
                      <>
                        <Grid>
                          <Button
                            variant="outlined"
                            onClick={handleAcceptSuggestion}
                            disabled={reviewSuggestionLoading}
                          >
                            Accept
                          </Button>
                        </Grid>
                        <Grid>
                          <Button
                            sx={{ mr: 1 }}
                            variant="outlined"
                            onClick={() => void handleDismissSuggestion()}
                            disabled={reviewSuggestionLoading}
                          >
                            Dismiss
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex' }}>
                      <Box sx={{ color: 'secondary.main', display: 'flex' }}>
                        {isRecommended && (
                          <ThumbUpIcon fontSize="small" style={{ color: '#00B580' }} />
                        )}
                        {isNotRecommended && (
                          <ThumbDownIcon fontSize="small" style={{ color: '#EB0000' }} />
                        )}
                        {isUnknownRecommendation && <RemoveCircleOutlineIcon fontSize="small" />}
                        {recommendationStatus && (
                          <Box sx={{ display: 'flex', ml: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: '600' }}>
                              {ProviderRecommendationStatus[recommendationStatus]}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {recommendationReasonLabels && (
                        <Box sx={{ ...RECOMMENDATION_TEXT_SX, ml: 1.5 }}>
                          {recommendationReasonLabels}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {locationConfidenceScore && (
                        <>
                          {/* // If location confidence score is 1,2 then show as low if greater then 2 then high*/}
                          {locationConfidenceScore > 2 ? (
                            <Box sx={{ display: 'flex', ml: 1 }}>
                              <Box sx={{ ...SOFT_SX, display: 'flex' }}>
                                <SignalCellular4BarOutlinedIcon />
                              </Box>
                              <Box
                                sx={{
                                  ...RECOMMENDATION_TEXT_SX,
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  ml: 1,
                                }}
                              >
                                High Certainty
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', ml: 1 }}>
                              <Box
                                style={{ verticalAlign: 'text-bottom' }}
                                sx={{ ...SOFT_SX, display: 'flex' }}
                              >
                                <SignalCellular1BarOutlinedIcon />
                              </Box>
                              <Box
                                sx={{
                                  ...RECOMMENDATION_TEXT_SX,
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  ml: 1,
                                }}
                              >
                                Low Certainty
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: '600' }}>
                        {' '}
                        {props?.provider?.name}
                      </Typography>
                      {props?.provider?.provider_type == 'In Home' ? (
                        <Box sx={{ display: 'flex' }}>
                          <Box
                            style={{ verticalAlign: 'text-bottom' }}
                            sx={{ ...SOFT_SX, display: 'flex', ml: 1 }}
                          >
                            <RoofingIcon fontSize="small" />
                          </Box>
                          <Box sx={{ ...RECOMMENDATION_TEXT_SX, display: 'flex', ml: 0.25 }}>
                            {props?.provider?.provider_type}
                          </Box>
                        </Box>
                      ) : props?.provider?.provider_type == 'At Home Test Kit' ? (
                        <Box sx={{ display: 'flex' }}>
                          <Box
                            style={{ verticalAlign: 'text-bottom' }}
                            sx={{ ...SOFT_SX, display: 'flex', ml: 1 }}
                          >
                            <InventoryIcon fontSize="small" />
                          </Box>
                          <Box sx={{ ...RECOMMENDATION_TEXT_SX, display: 'flex', ml: 0.25 }}>
                            {props?.provider?.provider_type}
                          </Box>
                        </Box>
                      ) : props?.provider?.provider_type == 'Virtual' ? (
                        <Box sx={{ display: 'flex' }}>
                          <Box
                            style={{ verticalAlign: 'text-bottom' }}
                            sx={{ ...SOFT_SX, display: 'flex', ml: 1 }}
                          >
                            <PhonelinkRingIcon fontSize="small" />
                          </Box>
                          <Box sx={{ ...RECOMMENDATION_TEXT_SX, display: 'flex', ml: 0.25 }}>
                            {props?.provider?.provider_type}
                          </Box>
                        </Box>
                      ) : (
                        distance != null && (
                          <Box sx={{ display: 'flex' }}>
                            <Box
                              style={{ verticalAlign: 'text-bottom' }}
                              sx={{ ...SOFT_SX, display: 'flex', ml: 1 }}
                            >
                              <PlaceIcon fontSize="small" />
                            </Box>
                            <Box sx={{ ...RECOMMENDATION_TEXT_SX, display: 'flex', ml: 0.25 }}>
                              {distance} Miles
                            </Box>
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                  {props.isSystemSuggested || props?.providerSearchType == 'procedure' ? null : (
                    <>
                      <IconButton sx={{ p: '0px' }} color="primary" onClick={handleUserClick}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        slotProps={{
                          list: {
                            disablePadding: true,
                          },
                        }}
                      >
                        <MenuItem onClick={handleEditProviderDetailsOpen}>Edit provider</MenuItem>
                        <MenuItem onClick={handleAvailabilityModalOpen}>Edit availability</MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
                {props?.provider?.npi && (
                  <Typography sx={SOFT_SX} variant="body2">
                    NPI {props?.provider?.npi}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mt: 1, pl: 1 }}>
                <Grid container direction="row" columnSpacing={1} sx={{ alignItems: 'flex-start' }}>
                  <Grid size={4}>
                    <Box sx={{ mb: 1 }}>
                      {(props?.providerSearchType != 'facility' || props.isSystemSuggested) &&
                        locationName &&
                        locationName != props?.provider?.name && (
                          // for facility search the name of the location is the same as
                          // as the name of the location and gets hoisted to
                          // the name element shown along with the distance
                          <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                            {locationName}
                          </Typography>
                        )}
                      {address?.address_line_1 && (
                        <Typography variant="body2">{address?.address_line_1}</Typography>
                      )}
                      {address?.address_line_2 && (
                        <Typography variant="body2">{address?.address_line_2} </Typography>
                      )}
                      <Typography variant="body2">
                        {address?.city}
                        {address?.city && address?.state ? ', ' : null}
                        {address?.state} {getFirstFiveZipStrict(address)}{' '}
                      </Typography>{' '}
                      {phone && <Typography variant="body2">Ph: {phone}</Typography>}
                      {Boolean(faxNumbers[0]?.trim()) && (
                        <Typography variant="body2">Fax: {faxNumbers.join(', ')}</Typography>
                      )}
                    </Box>
                    {isVerified && (
                      <Box sx={{ display: 'flex' }}>
                        <Box sx={{ display: 'flex', alignItems: 'end' }}>
                          <VerifiedUserIcon fontSize="small" />
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ display: 'flex', alignItems: 'flex-end', ml: 0.5 }}
                        >
                          Phone/Fax Verified
                        </Typography>
                      </Box>
                    )}
                    {networkPartnerAgreementType && (
                      <Grid container sx={{ alignItems: 'flex-start' }}>
                        <Box sx={{ mt: 1 }}>
                          <PermContactCalendar fontSize="small" />
                        </Box>
                        <Box sx={{ mt: 1, ml: 1 }}>
                          <Typography variant="subtitle2">
                            {getNetworkPartnerDisplayName(
                              networkPartnerAgreementType,
                              props?.provider?.partnership?.partnership_type,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {partnerName && partnerName != locationName && (
                      <Box>
                        <Typography variant="subtitle2">{'(' + partnerName + ')'}</Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid size={4}>
                    <Box sx={{ mb: 1, pl: 1 }}>
                      {props?.provider?.location_types && (
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={SOFT_SX} variant="body2">
                            {props?.provider?.location_types?.sort().join(', ')}
                          </Typography>
                        </Box>
                      )}
                      {props?.cptCode && (
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: '500' }}>
                            {props?.cptCode?.display}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={SOFT_SX} variant="body2">
                          {props?.provider?.specialties?.join(', ')}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={SOFT_SX} variant="body2">
                          {props?.provider?.languages?.join(', ')}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={SOFT_SX} variant="body2">
                          {props?.provider?.gender}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={4} sx={{ alignItems: 'flex-start' }}>
                    <Grid container>
                      {encounterPrice && (
                        <Grid container size={12} spacing={1}>
                          <Grid size={6}>
                            <Typography component="legend">Procedure Est. </Typography>
                          </Grid>
                          <Grid size={6}>
                            <Box sx={{ fontWeight: '600' }}>${encounterPrice}</Box>
                          </Grid>
                        </Grid>
                      )}
                      {invertedCostScore != null && (
                        <Grid container size={12} spacing={1}>
                          <Grid size={6}>
                            <Typography variant="body2" component="legend">
                              Cost
                            </Typography>
                          </Grid>
                          <Grid size={6}>
                            <StyledRating
                              name="read-only"
                              value={invertedCostScore}
                              size="small"
                              readOnly
                              icon={<AttachMoneyRoundedIcon fontSize="inherit" />}
                              emptyIcon={<Box></Box>}
                            />
                          </Grid>
                        </Grid>
                      )}
                      {expertiseScore != null && (
                        <Grid container size={12} spacing={1}>
                          <Grid size={6}>
                            <Typography variant="body2" component="legend">
                              Expertise
                            </Typography>
                          </Grid>
                          <Grid size={6}>
                            <StyledRating
                              name="read-only"
                              value={expertiseScore}
                              size="small"
                              readOnly
                            />
                          </Grid>
                        </Grid>
                      )}
                      {qualityScore != null && (
                        <Grid container size={12} spacing={1}>
                          <Grid size={6}>
                            <Typography variant="body2" component="legend">
                              Quality
                            </Typography>
                          </Grid>
                          <Grid size={6}>
                            <StyledRating
                              name="read-only"
                              value={qualityScore}
                              size="small"
                              readOnly
                            />
                          </Grid>
                        </Grid>
                      )}
                      {numberOfRatings != null && averageRating != null && (
                        <Grid container size={12} spacing={1}>
                          <Grid size={6}>
                            <Typography variant="body2" component="legend">
                              Ratings ({numberOfRatings})
                            </Typography>
                          </Grid>
                          <Grid size={6}>
                            <StyledRating
                              name="read-only"
                              value={averageRating}
                              size="small"
                              readOnly
                            />
                          </Grid>
                        </Grid>
                      )}

                      {(invertedCostScore != null ||
                        qualityScore != null ||
                        (numberOfRatings != null && averageRating != null)) && (
                        <Grid container size={12} spacing={1}>
                          <Grid size={12}>
                            <Box sx={{ minHeight: '8px' }}> </Box>
                          </Grid>
                        </Grid>
                      )}

                      {daysTillNextAvailability != null && doesProviderExist != false ? (
                        <Grid container size={12} spacing={1}>
                          <Grid size={12}>
                            <Typography variant="body2" component="legend">
                              <CalendarTodayIcon sx={AVAILABILITY_ICON_SX} />{' '}
                              {'Booking out ' + daysTillNextAvailability + ' days'}
                            </Typography>
                            <Typography variant="body2" component="legend">
                              {verifiedOn ? `(verified ${verifiedOn})` : null}
                            </Typography>
                          </Grid>
                        </Grid>
                      ) : doesProviderExist == false ? (
                        <Grid container size={12} spacing={1}>
                          <Grid size={12}>
                            <Typography variant="body2" component="legend">
                              <NotInterestedIcon sx={AVAILABILITY_ICON_SX} /> Not accepting patients
                            </Typography>
                            <Typography variant="body2" component="legend">
                              {verifiedOn ? `(verified ${verifiedOn})` : null}
                            </Typography>
                          </Grid>
                        </Grid>
                      ) : props.isSystemSuggested ||
                        props?.providerSearchType == 'procedure' ? null : (
                        <Grid container size={12} spacing={1}>
                          <Grid size={12}>
                            <Button component="span" onClick={handleAvailabilityModalOpen}>
                              + Add availability
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          {(props.npi || props.facilityName || props.providerName) && !locationInNetwork && (
            <Alert severity="warning">Provider may be out of network.</Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
